/**
 * Workspace との食い違いを出す節。
 *
 * **出さないと誰にも見えない。** 担当の表だけ見ていると「担当が設定されていないチーム」は
 * そこに行が無いので気付けず、「チームに当てられない Workspace の組織」は変換の時点で
 * 落ちている。どちらも人が直すものなので、見える場所に置く。
 *
 * **当てられない組織は「どこを直すか」で節に分ける。** Workspace で所属を設定する /
 * Backlog に選択肢を足す / cortex の対応表に足す——直す先が違うものを一つの束にすると、
 * 読んだ人が何をすればいいか分からない。節の順序と理由の並びは API が決めたものを
 * そのまま使う（画面に順序表を持つと、API 側を変えたときに黙ってずれる）。
 *
 * **「直さなくてよい」節は作らない。** 責任チームを持たない開発組織（`ACE` 配下）は
 * `devOrgs` として別に受け取り、記録済みのものとして既定で畳む。直せるものを
 * 「直さなくてよい」に積むと、人は一覧そのものを読まなくなる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 担当が設定されていないチームと、チームに当てられない Workspace の組織を「どこを直すか」ごとに分けて出す（記録済みの開発組織は畳む）
 * @graph-connects none
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import type {
  DevOrg,
  MissingTeam,
  UnmappedOrg,
  UnmappedOrgFixOwner,
} from '../../../lib/api/team-assignments.js';

/**
 * 直す先 → 理由の 2 段でまとめる。
 *
 * **並び順は API が返した順をそのまま使う**（出てきた順に節を作る）。画面に順序表を置くと
 * API 側の並べ替えを変えたときに黙ってずれるので、持たない。
 * @graph-connects none
 */
function groupByFixOwner(
  orgs: readonly UnmappedOrg[],
): { owner: UnmappedOrgFixOwner; reasons: { reason: string; orgs: UnmappedOrg[] }[] }[] {
  const owners: {
    owner: UnmappedOrgFixOwner;
    reasons: { reason: string; orgs: UnmappedOrg[] }[];
  }[] = [];
  for (const org of orgs) {
    let owner = owners.find((o) => o.owner === org.fixOwner);
    if (owner === undefined) {
      owner = { owner: org.fixOwner, reasons: [] };
      owners.push(owner);
    }
    let reason = owner.reasons.find((r) => r.reason === org.reason);
    if (reason === undefined) {
      reason = { reason: org.reason, orgs: [] };
      owner.reasons.push(reason);
    }
    reason.orgs.push(org);
  }
  return owners;
}

/**
 * 食い違いの 2 節。どちらも無ければ何も出さない。
 * @graph-connects none
 */
export function TeamAssignmentMismatches({
  missingTeams,
  unmappedOrgs,
  devOrgs,
  canManage,
  onAssign,
}: {
  missingTeams: readonly MissingTeam[];
  unmappedOrgs: readonly UnmappedOrg[];
  devOrgs: readonly DevOrg[];
  canManage: boolean;
  onAssign: (team: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const none = t('teamAssignments.none');
  const [showDevOrgs, setShowDevOrgs] = useState(false);

  const owners = groupByFixOwner(unmappedOrgs);
  /** 組織 1 件の行。3 欄のどれが空かも読めるように `-` で埋める。 */
  const orgRow = (org: UnmappedOrg | DevOrg): string =>
    t('teamAssignments.unmapped.row', {
      division: org.division ?? none,
      group: org.groupName ?? none,
      team: org.teamName ?? none,
      count: org.memberCount,
    });
  const orgKey = (org: UnmappedOrg | DevOrg): string =>
    `${org.division ?? ''}/${org.groupName ?? ''}/${org.teamName ?? ''}`;

  if (missingTeams.length === 0 && unmappedOrgs.length === 0 && devOrgs.length === 0) return null;

  return (
    <>
      {missingTeams.length > 0 && (
        <section className="mt-8">
          <h2 className="text-base font-semibold">{t('teamAssignments.missing.heading')}</h2>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {t('teamAssignments.missing.description')}
          </p>
          <ul className="mt-2 space-y-1">
            {missingTeams.map((item) => (
              <li className="flex items-center gap-3 text-sm" key={item.team}>
                <span>
                  {t('teamAssignments.missing.row', {
                    team: item.team,
                    count: item.currentMemberCount,
                  })}
                </span>
                {canManage && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      onAssign(item.team);
                    }}
                  >
                    {t('teamAssignments.missing.assign')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {unmappedOrgs.length > 0 && (
        <section className="mt-8">
          <h2 className="text-base font-semibold">{t('teamAssignments.unmapped.heading')}</h2>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {t('teamAssignments.unmapped.description')}
          </p>
          <div className="mt-2 space-y-4">
            {owners.map((owner) => (
              <div key={owner.owner}>
                <h3 className="text-sm font-semibold">
                  {t(`teamAssignments.unmapped.owner.${owner.owner}`)}
                </h3>
                {owner.reasons.map((group) => (
                  <div className="mt-1" key={group.reason}>
                    <p className="text-sm text-[var(--color-text-muted)]">
                      {t(`teamAssignments.unmapped.reason.${group.reason}`)}
                    </p>
                    <ul className="space-y-1 text-sm">
                      {group.orgs.map((org) => (
                        <li key={orgKey(org)}>{orgRow(org)}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      {devOrgs.length > 0 && (
        <section className="mt-8">
          <h2 className="text-base font-semibold">
            {t('teamAssignments.unmapped.devOrgs.heading')}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {t('teamAssignments.unmapped.devOrgs.description')}
          </p>
          <label className="mt-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showDevOrgs}
              onChange={(e) => {
                setShowDevOrgs(e.target.checked);
              }}
            />
            {t('teamAssignments.unmapped.devOrgs.show', { count: devOrgs.length })}
          </label>
          {showDevOrgs && (
            <ul className="mt-2 space-y-1 text-sm">
              {devOrgs.map((org) => (
                <li key={orgKey(org)}>{orgRow(org)}</li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}
